import React, {useCallback, useContext, useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCog, faDownload, faEye, faFileExcel, faFileInvoice, faSheetPlastic, faWallet} from "@fortawesome/free-solid-svg-icons";
import {faClock} from "@fortawesome/free-regular-svg-icons";
import {TransactionService} from "../../../services/transaction.service";
import {toast} from "react-toastify";
import {UserContext} from "../../../context/UserContext";
import GraphComponent from "./graph/GraphComponent";
import {
    CountryAnalyticDTO, CountryTopupAnalyticDTO, CountryUsageAnalyticDTO, MonthlyAnalyticDTO,
} from "../../../dto/AnalyticDTO";
import {AnalyticsService} from "../../../services/analytics.service";
import {CircleFlag} from "react-circle-flags";
import {TabType} from "../dashboard/tab/AbstractTabTypes";
import {IDashboardSummary} from "../../../services/responses/dashboard.responses";
import CampaignDailyGraph from "./graph/CampaignDailyGraph";
import DynamicLinkGraph from "./graph/DynamicLinkGraph";
import { DatePicker, Button, Dropdown, Menu, Tooltip, Popover, Modal, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import CountryModal from "./modals/CountryModal";
import { CAMPAIGN_ID } from "../../../utils/config";

const { RangePicker } = DatePicker;

const AnalyticsPanel: React.FC<any> = ({}) => {

    const {user} = useContext(UserContext);

    const [loading, setLoading] = useState<boolean>(true);
    const [summaryData, setSummaryData] = useState<IDashboardSummary>();

    const [analyticsLoading, setAnalyticsLoading] = useState<boolean>(true);
    const [topCountries, setTopCountries] = useState<CountryAnalyticDTO[]>([]);
    const [topUsageCountries, setTopUsageCountries] = useState<CountryUsageAnalyticDTO[]>([]);
    const [topTopupCountries, setTopTopupCountries] = useState<CountryTopupAnalyticDTO[]>([]);
    const [monthAnalytics, setMonthAnalytics] = useState<MonthlyAnalyticDTO[]>([]);

    // Date range state
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(7, 'day'),
        dayjs()
    ]);

    // Modal visibility states
    const [showCountriesModal, setShowCountriesModal] = useState(false);
    const [showUsageModal, setShowUsageModal] = useState(false);
    const [showTopupModal, setShowTopupModal] = useState(false);

    // Export activated eSIMs modal
    const [showExportModal, setShowExportModal] = useState(false);
    const [campaignId, setCampaignId] = useState<string>(CAMPAIGN_ID || "");
    const [exportDateRange, setExportDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(30, 'day'),
        dayjs()
    ]);
    const [exportLoading, setExportLoading] = useState(false);

    const customId = "custom-simly-toast";

    const toastOptions = {
        position: toast.POSITION.TOP_CENTER,
        toastId: customId,
        autoClose: 3000,
        closeOnClick: true,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
        draggable: false
    }

    const fetchSummary = async () => {
        try {
            const startDate = dateRange[0].format('YYYY-MM-DD');
            const endDate = dateRange[1].format('YYYY-MM-DD');
            const data = await TransactionService.getDashboardSummary(startDate, endDate);
            setSummaryData(data.data);
            setLoading(false);
        } catch (error) {
            toast.error('Unable to fetch the dashboard summary.', toastOptions);
            setLoading(false);
        }
    }

    const fetchAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        try {
            const startDate = dateRange[0].format('YYYY-MM-DD');
            const endDate = dateRange[1].format('YYYY-MM-DD');

            await Promise.all([
                AnalyticsService.getCountryAnalytics(startDate, endDate).then(r => setTopCountries(r.data)),
                AnalyticsService.getCountryUsageAnalytics(startDate, endDate).then(r => setTopUsageCountries(r.data)),
                AnalyticsService.getCountryTopupAnalytics(startDate, endDate).then(r => setTopTopupCountries(r.data)),
                AnalyticsService.getMonthlyAnalytics(startDate, endDate).then(r => setMonthAnalytics(r.data))
            ]);

            setAnalyticsLoading(false);
        } catch (error) {
            toast.error('Unable to fetch analytics data.', toastOptions);
            setAnalyticsLoading(false);
        }
    }, [dateRange])

    useEffect(() => {
        fetchSummary();
        fetchAnalytics();
    }, [dateRange])

    // Export data to Excel
    const exportToExcel = () => {
        try {
            // Create workbook and worksheets
            const wb = XLSX.utils.book_new();

            // Add countries data
            if (topCountries.length > 0) {
                const countriesData = topCountries.map(item => ({
                    'Country': item.country.name,
                    'ISO Code': item.country.isoCode,
                    'Count': item.count
                }));
                const wsCountries = XLSX.utils.json_to_sheet(countriesData);
                XLSX.utils.book_append_sheet(wb, wsCountries, "Top Countries");
            }

            // Add usage data
            if (topUsageCountries.length > 0) {
                const usageData = topUsageCountries.map(item => ({
                    'Country': item.country.name,
                    'ISO Code': item.country.isoCode,
                    'Total Consumed (GB)': item.totalConsumed
                }));
                const wsUsage = XLSX.utils.json_to_sheet(usageData);
                XLSX.utils.book_append_sheet(wb, wsUsage, "Top Country Usages");
            }

            // Add topup data
            if (topTopupCountries.length > 0) {
                const topupData = topTopupCountries.map(item => ({
                    'Country': item.country.name,
                    'ISO Code': item.country.isoCode,
                    'Total Amount Spent ($)': item.totalAmountSpent
                }));
                const wsTopup = XLSX.utils.json_to_sheet(topupData);
                XLSX.utils.book_append_sheet(wb, wsTopup, "Top Topup Countries");
            }

            // Generate filename with date range
            const startDateStr = dateRange[0].format('YYYYMMDD');
            const endDateStr = dateRange[1].format('YYYYMMDD');
            const fileName = `Analytics_${startDateStr}_to_${endDateStr}.xlsx`;

            // Save the file
            XLSX.writeFile(wb, fileName);
            toast.success('Analytics data exported successfully!', toastOptions);
        } catch (error) {
            toast.error('Failed to export data.', toastOptions);
            console.error('Export error:', error);
        }
    };

    // Export activated eSIMs
    const exportActivatedSims = async () => {
        if (!campaignId.trim()) {
            toast.error('Please enter a valid Campaign ID.', toastOptions);
            return;
        }

        try {
            setExportLoading(true);
            const startDate = exportDateRange[0].format('YYYY-MM-DD');
            const endDate = exportDateRange[1].format('YYYY-MM-DD');

            // Use the AnalyticsService to get the blob data
            const blobData = await AnalyticsService.exportActivatedSims(
                campaignId,
                startDate,
                endDate
            );

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blobData);

            // Create a temporary link element to trigger the download
            const link = document.createElement('a');
            link.href = url;
            link.download = `activated_sims_${campaignId}_${startDate}_to_${endDate}.csv`;

            // Append to the document body and trigger the download
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Activated eSIMs exported successfully!', toastOptions);
            setShowExportModal(false);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export activated eSIMs.', toastOptions);
        } finally {
            setExportLoading(false);
        }
    };

    const exportMenu = (
        <Menu>
            <Menu.Item key="analytics" onClick={exportToExcel}>
                <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                Export Analytics Data
            </Menu.Item>
            <Menu.Item key="activated-sims" onClick={() => setShowExportModal(true)}>
                <FontAwesomeIcon icon={faFileInvoice} className="mr-2" />
                Export Activated eSIMs
            </Menu.Item>
        </Menu>
    );

    return (
        <div className={"w-full h-full"}>
            <div className={"w-full h-full flex flex-col items-center justify-center mt-2"}>
                <div className={"w-11/12 h-16 sm:h-24 flex flex-row items-center justify-between"}>
                    <p className={"text-base font-bold sm:text-xl"}>Dashboard</p>
                    <Dropdown overlay={exportMenu} placement="bottomRight">
                        <button className={"w-24 h-8 sm:w-32 sm:h-10 flex flex-row items-center justify-center bg-blue-500 rounded-lg gap-2"}>
                            <FontAwesomeIcon icon={faFileExcel} className="text-white" />
                            <p className={"text-white text-xs"}>Export</p>
                        </button>
                    </Dropdown>
                </div>
                <div className={"w-11/12 h-16 sm:h-24 flex flex-row items-center justify-between"}>
                    <p className={"text-[10px] font-bold"}>SUMMARY</p>
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => {
                            if (dates) setDateRange(dates as [Dayjs, Dayjs]);
                        }}
                        allowClear={false}
                    />
                </div>
                <div
                    className={"w-11/12 h-64 sm:h-16 flex flex-col sm:flex-row items-center justify-between mt-5 sm:mt-0"}>
                    <div
                        className={"w-9/12 sm:w-[32.5%] h-16 sm:h-24 flex flex-row border border-gray-200 rounded-xl items-center justify-center"}>
                        <div className={"w-10/12 xl:w-[95%] h-full flex flex-row items-center justify-start"}>
                            <div
                                className={"w-8 h-8 xl:w-12 xl:h-12 rounded-lg flex flex-row items-center justify-center bg-gray-100"}>
                                <FontAwesomeIcon className={"text-xs xl:text-base"} icon={faCog}/>
                            </div>

                            <div className={"w-16 h-9 xl:h-14 flex flex-col ml-4 xl:mt-2"}>
                                <p className={"text-xs text-gray-500 font-semibold"}>Generated</p>
                                {loading ? <div
                                        className={"animate-pulse h-8 sm:h-4 w-32 bg-gray-200 rounded mt-2 sm:mt:4"}></div> :
                                    <p className={"text-base xl:text-2xl font-bold"}>{summaryData?.generated}</p>}
                            </div>
                        </div>
                    </div>
                    <div
                        className={"w-9/12 sm:w-[32.5%] h-16 sm:h-24 flex flex-row border border-gray-200 rounded-xl items-center justify-center"}>
                        <div className={"w-10/12 xl:w-[95%] h-full flex flex-row items-center justify-start"}>
                            <div
                                className={"w-8 h-8 xl:w-12 xl:h-12 rounded-lg flex flex-row items-center justify-center bg-gray-100"}>
                                <FontAwesomeIcon className={"text-xs xl:text-base"} icon={faDownload}/>
                            </div>

                            <div className={"w-16 h-9 xl:h-14 flex flex-col ml-4 xl:mt-2"}>
                                <p className={"text-xs text-gray-500 font-semibold"}>Installed</p>
                                {loading ? <div
                                        className={"animate-pulse h-8 sm:h-4 w-32 bg-gray-200 rounded mt-2 sm:mt:4"}></div> :
                                    <p className={"text-base xl:text-2xl font-bold"}>{summaryData?.installed}</p>}
                            </div>
                        </div>
                    </div>
                    <div
                        className={"w-9/12 sm:w-[32.5%] h-16 sm:h-24 flex flex-row border border-gray-200 rounded-xl items-center justify-center"}>
                        <div className={"w-10/12 xl:w-[95%] h-full flex flex-row items-center justify-start"}>
                            <div
                                className={"w-8 h-8 xl:w-12 xl:h-12 rounded-lg flex flex-row items-center justify-center bg-gray-100"}>
                                <FontAwesomeIcon className={"text-xs xl:text-base"} icon={faClock}/>
                            </div>

                            <div className={"w-full h-9 xl:h-14 flex flex-col ml-4 xl:mt-2"}>
                                <p className={"text-xs text-gray-500 font-semibold"}>Pending</p>
                                {loading ?
                                    <div className={"animate-pulse h-4 w-32 bg-gray-200 rounded mt-2 sm:mt:4"}></div> :
                                    <p className={"text-base xl:text-2xl font-bold"}>{summaryData?.pending}</p>}
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className={"w-11/12 h-[62rem] sm:h-[24rem] flex flex-col sm:flex-row items-center justify-between mt-5 sm:mt-0"}>
                    <div
                        className={"w-9/12 sm:w-[32.5%] h-[19.5rem] flex flex-col border border-gray-200 rounded-2xl items-center"}>
                        <div className={"w-[95%] h-7 flex flex-row justify-between items-center mt-3"}>
                            <p className={"text-sm sm:text-xs lg:text-sm font-semibold text-gray-600"}>Top Countries</p>
                            <Button
                                type="text"
                                onClick={() => setShowCountriesModal(true)}
                                className="flex items-center"
                                icon={<FontAwesomeIcon icon={faEye} className="mr-1" />}
                                size="small"
                            >
                                View More
                            </Button>
                        </div>
                        <div className={"w-full h-[0.09rem] bg-gray-200 mt-1"}></div>

                        <div className={"w-[95%] h-60 flex flex-col gap-2 mt-3"}>
                            {analyticsLoading ? (<>
                                {[...Array(6)].map((_, i) =>
                                    <div key={i} className={"flex flex-row ml-2 w-full h-8 justify-start items-center"}>
                                        <div
                                            className={"animate-pulse bg-gray-200 rounded-full w-4 h-4 lg:w-6 lg:h-6"}/>
                                        <div
                                            className={"ml-3 animate-pulse bg-gray-200 w-[80%] lg:w-[90%] h-4 lg:h-6 rounded-xl"}></div>
                                    </div>
                                )} </>) : <>
                                {topCountries.slice(0, 6).map((country, idx) =>
                                    <div key={idx} className={"flex flex-row ml-2 w-full h-8 justify-between items-center"}>
                                        <div className={"flex flex-row w-auto h-8 items-center justify-between"}>
                                            <img alt={country.country.name} src={`${country.country.countryImageURL}`}
                                                 className={"w-4 h-4 lg:w-6 lg:h-6"}/>
                                            <p className={"text-sm w-28 lg:w-auto lg:text-base ml-2 lg:ml-3 text-gray-700 truncate"}>{country.country.name}</p>
                                        </div>

                                        <p className={"text-sm lg:text-base font-bold mr-4 lg:mr-2"}>{country.count}</p>
                                    </div>
                                )}
                            </>}
                        </div>
                    </div>
                    <div
                        className={"w-9/12 sm:w-[32.5%] h-[19.5rem] flex flex-col border border-gray-200 rounded-2xl items-center"}>
                        <div className={"w-[95%] h-7 flex flex-row justify-between items-center mt-3"}>
                            <p className={"text-sm sm:text-xs lg:text-sm font-semibold text-gray-600"}>Top Country Usages</p>
                            <Button
                                type="text"
                                onClick={() => setShowUsageModal(true)}
                                className="flex items-center"
                                icon={<FontAwesomeIcon icon={faEye} className="mr-1" />}
                                size="small"
                            >
                                View More
                            </Button>
                        </div>
                        <div className={"w-full h-[0.09rem] bg-gray-200 mt-1"}></div>

                        <div className={"w-[95%] h-60 flex flex-col mt-3 gap-2"}>
                            {analyticsLoading ? (<>
                                {[...Array(6)].map((_, i) =>
                                    <div key={i} className={"flex flex-row ml-2 w-full h-8 justify-start items-center"}>
                                        <div
                                            className={"animate-pulse bg-gray-200 rounded-full w-4 h-4 lg:w-6 lg:h-6"}/>
                                        <div
                                            className={"ml-3 animate-pulse bg-gray-200 w-[80%] lg:w-[90%] h-4 lg:h-6 rounded-xl"}></div>
                                    </div>
                                )} </>) : <>
                                {topUsageCountries.slice(0, 6).map((country, idx) =>
                                    <div key={idx} className={"flex flex-row ml-2 w-full h-8 justify-between items-center"}>
                                        <div className={"flex flex-row w-auto h-8 items-center justify-between"}>
                                            <img alt={country.country.name} src={`${country.country.countryImageURL}`}
                                                 className={"w-4 h-4 lg:w-6 lg:h-6"}/>
                                            <p className={"text-sm w-28 lg:w-auto lg:text-base ml-2 lg:ml-3 text-gray-700 truncate"}>{country.country.name}</p>
                                        </div>

                                        <p className={"text-sm lg:text-base font-bold mr-4 lg:mr-2"}>{country.totalConsumed + " GB"}</p>
                                    </div>
                                )}
                            </>}
                        </div>
                    </div>
                    <div
                        className={"w-9/12 sm:w-[32.5%] h-[19.5rem] flex flex-col border border-gray-200 rounded-2xl items-center"}>
                        <div className={"w-[95%] h-7 flex flex-row justify-between items-center mt-3"}>
                            <p className={"text-sm sm:text-xs lg:text-sm font-semibold text-gray-600"}>Top Country Topups</p>
                            <Button
                                type="text"
                                onClick={() => setShowTopupModal(true)}
                                className="flex items-center"
                                icon={<FontAwesomeIcon icon={faEye} className="mr-1" />}
                                size="small"
                            >
                                View More
                            </Button>
                        </div>
                        <div className={"w-full h-[0.09rem] bg-gray-200 mt-1"}></div>

                        <div className={"w-[95%] h-60 flex flex-col gap-2 mt-3"}>
                            {analyticsLoading ? (<>
                                {[...Array(6)].map((_, i) =>
                                    <div key={i} className={"flex flex-row ml-2 w-full h-8 justify-start items-center"}>
                                        <div
                                            className={"animate-pulse bg-gray-200 rounded-full w-4 h-4 lg:w-6 lg:h-6"}/>
                                        <div
                                            className={"ml-3 animate-pulse bg-gray-200 w-[80%] lg:w-[90%] h-4 lg:h-6 rounded-xl"}></div>
                                    </div>
                                )} </>) : <>
                                {topTopupCountries.slice(0, 6).map((country, idx) =>
                                    <div key={idx} className={"flex flex-row ml-2 w-full h-8 justify-between items-center"}>
                                        <div className={"flex flex-row w-auto h-8 items-center justify-between"}>
                                            <img alt={country.country.name} src={`${country.country.countryImageURL}`}
                                                 className={"w-4 h-4 lg:w-6 lg:h-6"}/>
                                            <p className={"text-sm w-28 lg:w-auto lg:text-base ml-2 lg:ml-3 text-gray-700 truncate"}>{country.country.name}</p>
                                        </div>

                                        <p className={"text-sm lg:text-base font-bold mr-4 lg:mr-2"}>${country.totalAmountSpent}</p>
                                    </div>
                                )}
                            </>}
                        </div>
                    </div>
                </div>

                <div className={"w-11/12 flex flex-row justify-between gap-4"}>
                    <div className={"w-[45%]"}>
                        <CampaignDailyGraph/>
                    </div>
                    <div className={"w-[45%]"}>
                        <DynamicLinkGraph/>
                    </div>
                </div>
            </div>

            {/* Existing Modals */}
            <CountryModal<CountryAnalyticDTO>
                isVisible={showCountriesModal}
                onClose={() => setShowCountriesModal(false)}
                title="All Countries"
                data={topCountries}
                dataType="count"
            />

            <CountryModal<CountryUsageAnalyticDTO>
                isVisible={showUsageModal}
                onClose={() => setShowUsageModal(false)}
                title="All Country Usages"
                data={topUsageCountries}
                dataType="usage"
            />

            <CountryModal<CountryTopupAnalyticDTO>
                isVisible={showTopupModal}
                onClose={() => setShowTopupModal(false)}
                title="All Country Topups"
                data={topTopupCountries}
                dataType="topup"
            />

            {/* Export Activated eSIMs Modal */}
            <Modal
                title="Export Activated eSIMs"
                open={showExportModal}
                onCancel={() => setShowExportModal(false)}
                footer={[
                    <Button key="cancel" onClick={() => setShowExportModal(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="export"
                        type="primary"
                        loading={exportLoading}
                        onClick={exportActivatedSims}
                    >
                        Export
                    </Button>
                ]}
            >
                <div className="py-4">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date Range
                        </label>
                        <RangePicker
                            value={exportDateRange}
                            onChange={(dates) => {
                                if (dates) setExportDateRange(dates as [Dayjs, Dayjs]);
                            }}
                            className="w-full"
                            allowClear={false}
                        />
                    </div>
                    <div className="text-sm text-gray-500">
                        <p>This will export all eSIMs that were activated between the selected dates for the specified campaign.</p>
                        <p className="mt-2">The export includes transaction details like IDs, dates, prices, and eSIM details such as ICCID, QR codes, and activation dates.</p>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default AnalyticsPanel;
